// London Smoke & Cure — pure customer pricing key helpers (no Firebase).
// Used by SPA forms, cart, and standing-order schedule helpers without loading Auth/Firestore.

/** Built-in tiers — custom keys are any other pricing_sheets / products_* list. */
export const STANDARD_PRICING_KEYS = Object.freeze(['default', 'a', 'b', 'c']);

/**
 * Master product/image/SKU catalogue (`products_default`).
 * Not a customer wholesale price list — operators copy from here into A/B/C (and custom) sheets.
 */
export const MASTER_CATALOG_PRICING_KEY = 'default';

/** When users.Pricing is missing, blank, or legacy "default" (wholesale accounts). */
export const DEFAULT_WHOLESALE_PRICING_KEY = 'a';

/** Anonymous / trade brochure / public home product strip. */
export const GUEST_PRICING_KEY = 'a';

/**
 * Normalize any raw tier token to a lowercase Firestore key.
 * @param {unknown} raw
 * @param {{ fallback?: string }} [options]
 */
export function normalizePricingKey(raw, { fallback = DEFAULT_WHOLESALE_PRICING_KEY } = {}) {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return String(fallback).trim().toLowerCase();
    return trimmed.toLowerCase();
}

/**
 * Customer-facing price list key.
 * Maps legacy / mistaken `default` (master catalogue) onto Pricing A.
 * Pass `{ allowMasterCatalog: true }` only for Product Management / stock tooling.
 */
export function coerceCustomerPricingKey(raw, options = {}) {
    const { allowMasterCatalog = false, fallback = DEFAULT_WHOLESALE_PRICING_KEY } = options;
    const key = normalizePricingKey(raw, { fallback });
    if (!allowMasterCatalog && key === MASTER_CATALOG_PRICING_KEY) {
        return DEFAULT_WHOLESALE_PRICING_KEY;
    }
    return key;
}

/** Short label for admin UI (A, B, C, custom…). */
export function formatCustomerPricingLabel(raw) {
    const key = coerceCustomerPricingKey(raw);
    if (key === 'a') return 'A (standard wholesale)';
    if (key === 'b') return 'B';
    if (key === 'c') return 'C';
    return key;
}

/**
 * Resolve tier from a users/{uid} document (or partial user object).
 * Reads canonical Pricing first; legacy pricing / pricingTier / pricingKey only as fallback.
 * Legacy `Pricing: "default"` is treated as Pricing A (master catalogue is not a customer tier).
 */
export function resolvePricingKeyFromUserData(userData, options) {
    const allowMasterCatalog = options?.allowMasterCatalog === true;
    const coerceOpts = {
        allowMasterCatalog,
        fallback: options?.fallback ?? DEFAULT_WHOLESALE_PRICING_KEY,
    };
    if (!userData || typeof userData !== 'object') {
        return coerceCustomerPricingKey(null, coerceOpts);
    }
    const canonical = userData.Pricing;
    if (canonical != null && String(canonical).trim()) {
        return coerceCustomerPricingKey(canonical, coerceOpts);
    }
    const legacy = userData.pricing ?? userData.pricingTier ?? userData.pricingKey;
    if (legacy != null && String(legacy).trim()) {
        return coerceCustomerPricingKey(legacy, coerceOpts);
    }
    return coerceCustomerPricingKey(null, coerceOpts);
}
